/**
 * TimeSlots Component
 * Display available time slots for a selected date
 */

import { Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface TimeSlot {
    start_time: string;
    end_time: string;
    available: boolean;
}

interface TimeSlotsProps {
    slots: TimeSlot[];
    selectedSlot?: TimeSlot;
    onSlotSelect: (slot: TimeSlot) => void;
    loading?: boolean;
    timezone?: string;
}

export function TimeSlots({ slots, selectedSlot, onSlotSelect, loading, timezone = 'UTC' }: TimeSlotsProps) {
    const formatTime = (isoString: string) => {
        const date = new Date(isoString);
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: timezone,
        });
    };

    if (loading) {
        return (
            <Card>
                <CardContent className="py-8">
                    <div className="flex flex-col items-center justify-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        <p className="text-sm text-muted-foreground">Loading available times...</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (slots.length === 0) {
        return (
            <Card>
                <CardContent className="py-8">
                    <div className="flex flex-col items-center justify-center gap-3 text-center">
                        <Clock className="h-12 w-12 text-muted-foreground" />
                        <div>
                            <h3 className="font-semibold mb-1">No times available</h3>
                            <p className="text-sm text-muted-foreground">
                                Please select a different date
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            {/* Timezone info */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Times shown in {timezone}</span>
            </div>

            {/* Time slots grid - Responsive */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {slots.map((slot, index) => {
                    const startTime = new Date(slot.start_time);
                    const isSelected = selectedSlot?.start_time === slot.start_time;

                    return (
                        <Button
                            key={index}
                            variant={isSelected ? 'default' : 'outline'}
                            className="w-full text-sm py-5"
                            onClick={() => onSlotSelect(slot)}
                        >
                            {startTime.toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                                timeZone: timezone,
                            })}
                        </Button>
                    );
                })}
            </div>

            {/* Selected slot info */}
            {selectedSlot && (
                <Card className="bg-muted/50">
                    <CardContent className="py-3">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Selected time:</span>
                            <span className="font-semibold">
                                {formatTime(selectedSlot.start_time)} - {formatTime(selectedSlot.end_time)}
                            </span>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
